/**
 * loadOcclusionMesh: load a GLB as an invisible depth-only occluder.
 *
 * The same collision proxy (scene.collision.glb) is reused: its meshes are
 * rendered with `colorWrite=false` / `depthWrite=true` on the Occlusion layer
 * so they write depth but no color. Custom World meshes drawn afterwards are
 * hidden behind walls/columns without the gaussian splat itself having to
 * participate in depth. The occluder is additionally drawn on the AvatarDepth
 * layer so the local third-person avatar is occluded by scene geometry too
 * (2026-09-28 用户要求遮挡一致性；代价：碰撞代理过填充区域贴墙时可能
 * 轻微裁到人物边角，视为可接受)。
 *
 * Non-fatal: returns null on any failure so the scene still runs without
 * occlusion. The asset is cached by URL in the registry (shared with any other
 * container loader hitting the same URL).
 */

import { Asset, CULLFACE_NONE, Entity, StandardMaterial } from 'playcanvas';
import type { Application } from 'playcanvas';
import { getLayers } from '../render/LayerManager';

function nowMs(): number {
    return typeof performance !== 'undefined' ? performance.now() : Date.now();
}

export interface OcclusionHandle {
    entity: Entity;
    asset: Asset;
}

/** Shared depth-only material (no color write, writes depth only). */
function makeDepthOnlyMaterial(): StandardMaterial {
    const mat = new StandardMaterial();
    mat.name = 'DepthOnlyOcclusion';
    mat.diffuse.set(0, 0, 0);
    mat.emissive.set(0, 0, 0);
    mat.useLighting = false;
    // Keep the depth proxy independent from scene skybox / IBL state; WebGPU
    // can invalidate StandardMaterial bind groups when a skybox env map appears.
    mat.useSkybox = false;
    mat.useFog = false;
    mat.useTonemap = false;
    // Disable all color writes so the mesh is invisible; only depth is written.
    mat.redWrite = false;
    mat.greenWrite = false;
    mat.blueWrite = false;
    mat.alphaWrite = false;
    mat.depthWrite = true;
    mat.depthTest = true;
    // Generated wall-grid occluders can face either direction, so write depth double-sided.
    mat.cull = CULLFACE_NONE;
    mat.update();
    return mat;
}

export function loadOcclusionMesh(app: Application, url: string): Promise<OcclusionHandle | null> {
    return new Promise((resolve) => {
        const startedAt = nowMs();
        let lastProgressLogMs = Number.NEGATIVE_INFINITY;
        const existing = app.assets.getByUrl(url);
        const asset = existing ?? new Asset(url, 'container', { url });
        console.info(`[occlusion] start url=${url} cached=${Boolean(existing)} loaded=${Boolean(asset.loaded)}`);
        if (!existing) {
            app.assets.add(asset);
            asset.on('progress', (received: number, total: number) => {
                const progressAt = nowMs();
                if (progressAt - lastProgressLogMs < 1000) return;
                lastProgressLogMs = progressAt;
                console.info(
                    `[occlusion] progress url=${url}` +
                    ` received=${Math.round(received ?? 0)} total=${Math.round(total ?? 0)}` +
                    ` elapsedMs=${Math.round(progressAt - startedAt)}`
                );
            });
            app.assets.load(asset);
        }

        const finish = (ok: boolean) => {
            const loadedAt = nowMs();
            if (!ok) {
                // Don't unload a shared/cached asset another system may hold.
                console.warn(`[occlusion] failed url=${url} elapsedMs=${Math.round(loadedAt - startedAt)}`);
                resolve(null);
                return;
            }
            console.info(`[occlusion] asset loaded url=${url} elapsedMs=${Math.round(loadedAt - startedAt)}`);
            const applyStartedAt = nowMs();
            const resource = asset.resource as
                | { instantiate?: () => Entity; renders?: unknown[] }
                | undefined;
            if (!resource) {
                // 常规不应发生（容器常驻注册表）；出现即说明有别的加载器把共享
                // 资产 unload 了——大声报出来，不再静默丢遮挡体。
                console.warn(`[occlusion] container resource missing after load url=${url} (asset unloaded elsewhere?)`);
                resolve(null);
                return;
            }

            const mat = makeDepthOnlyMaterial();
            const layers = getLayers();
            // Occlusion 层：为场景物体（World 层）提供遮挡深度；AvatarDepth 层：
            // 清空场景深度后为本地玩家 avatar 重新补写遮挡深度（该层是「允许
            // 遮挡本地人物」的白名单——远程玩家真实网格/GLB 物体/场景遮挡体）。
            const layerIds = layers ? [layers.occlusion.id, layers.avatarDepth.id] : [];

            let entity: Entity | null = null;
            if (typeof resource.instantiate === 'function') {
                entity = resource.instantiate();
            } else if (Array.isArray(resource.renders) && resource.renders.length) {
                // Fallback: a single render component holding the first render asset.
                entity = new Entity('OcclusionMesh');
                entity.addComponent('render', { asset: resource.renders[0] as Asset });
            }
            if (!entity) {
                console.warn(`[occlusion] container exposes neither instantiate() nor renders url=${url}`);
                resolve(null);
                return;
            }

            entity.name = 'OcclusionMesh';
            let renderNodeCount = 0;
            let meshCount = 0;
            entity.forEach((node) => {
                const e = node as Entity;
                if (e.render) {
                    renderNodeCount++;
                    e.render.material = mat;
                    if (layerIds.length) e.render.layers = layerIds;
                    // Also force the per-meshInstance material in case the render
                    // component assigned asset materials on instantiate.
                    const mi = (e.render as unknown as { meshInstances?: { material: unknown }[] }).meshInstances;
                    if (mi) {
                        meshCount += mi.length;
                        for (const m of mi) m.material = mat;
                    }
                }
            });

            app.root.addChild(entity);
            console.info(
                `[occlusion] applied url=${url} renderNodes=${renderNodeCount} meshes=${meshCount}` +
                ` applyMs=${Math.round(nowMs() - applyStartedAt)} totalMs=${Math.round(nowMs() - startedAt)}`
            );
            resolve({ entity, asset });
        };

        if (asset.loaded) finish(true);
        else {
            asset.on('load', () => finish(true));
            asset.on('error', () => finish(false));
        }
    });
}
